import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Users, Eye, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/admin/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/admin/courses/${deleteId}`);
      toast.success('Curso eliminado');
      setCourses(courses.filter(c => c.id !== deleteId));
    } catch (error) {
      toast.error('Error al eliminar el curso');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/admin" 
            className="p-2 rounded-full hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))]" data-testid="admin-courses-title">
              Gestión de Cursos
            </h1>
            <p className="text-[hsl(var(--text-secondary))]">
              {courses.length} cursos en total
            </p>
          </div>
          <Link to="/admin/cursos/nuevo">
            <Button className="btn-gold flex items-center gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Nuevo curso
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[hsl(var(--text-secondary))]">Cargando cursos...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="card-soft text-center p-12">
            <p className="text-[hsl(var(--text-secondary))] mb-6">No hay cursos creados</p>
            <Link to="/admin/cursos/nuevo">
              <Button className="btn-gold">Crear primer curso</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {courses.map((course) => (
              <div 
                key={course.id}
                className="card-soft flex flex-col md:flex-row gap-6"
                data-testid={`admin-course-${course.id}`}
              >
                {/* Image */}
                <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={course.image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          course.is_published 
                            ? 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {course.is_published ? 'Publicado' : 'Borrador'}
                        </span>
                        <span className="text-sm text-[hsl(var(--text-tertiary))]">
                          {course.duration}
                        </span>
                      </div>
                      <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-1">
                        {course.title}
                      </h3>
                      <p className="text-sm text-[hsl(var(--text-secondary))] line-clamp-2 mb-4">
                        {course.description}
                      </p>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/formaciones/${course.id}`} className="flex items-center gap-2">
                            <Eye className="w-4 h-4" /> Ver público
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/cursos/${course.id}`} className="flex items-center gap-2">
                            <Edit className="w-4 h-4" /> Editar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/cursos/${course.id}/alumnos`} className="flex items-center gap-2">
                            <Users className="w-4 h-4" /> Ver alumnos
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeleteId(course.id)}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                      <span className="text-[hsl(var(--text-secondary))]">
                        {course.enrollments_count || 0} alumnos
                      </span>
                    </div>
                    <div className="text-[hsl(var(--accent))] font-heading text-xl">
                      {course.price}€
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este curso?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El curso será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCourses;
